import type { FunctionComponent } from "react";
import { graphql, useFragment } from "react-relay";

import { withHighlightedHeadline } from "scmp-app/components/content/content-item-render/highlight/hoc";
import { useContentItemProviders } from "scmp-app/components/content/content-item-render/hooks";
import { EntityLink } from "scmp-app/components/entity-link";
import { useMyDailyFiveArticle } from "scmp-app/components/my-news/my-daily-five/hooks";
import type { feedViewArticleContent$key } from "scmp-app/queries/__generated__/feedViewArticleContent.graphql";

import {
  ActionBar,
  Container,
  ContentWrapper,
  CoverContainer,
  Headline,
  Summary,
  TopicLink,
  Wrapper,
} from "./styles";

export type Props = {
  className?: string;
  onClick?: (entityId?: string) => void;
  reference: feedViewArticleContent$key;
};

export const Component: FunctionComponent<Props> = ({ className, onClick, reference }) => {
  const content = useFragment(
    graphql`
      fragment feedViewArticleContent on Content {
        ...entityLink
        ...hooksUseMyDailyFiveContent
        ...hooksContentItemProvidersContent
          @arguments(
            withTopicLink: true
            withHeadline: true
            withSummary: true
            withCoverImage: true
            withCoverImageSize640x360: true
            withActionBar: true
          )
        entityId
      }
    `,
    reference,
  );

  const isTrendingVisible = useMyDailyFiveArticle(content);
  const providers = useContentItemProviders(content, { type: "main" });

  return (
    <Container className={className}>
      <Wrapper>
        <ContentWrapper>
          <TopicLink>{providers.topicLink?.()}</TopicLink>
          <EntityLink onClick={() => onClick?.(content.entityId)} reference={content}>
            <Headline>{providers.headline({ preferSocialHeadline: true })}</Headline>
          </EntityLink>
          <Summary>{providers.summary()}</Summary>
          <ActionBar>
            {providers.actionBar({
              isIconVisible: false,
              isTrendingVisible,
            })}
          </ActionBar>
        </ContentWrapper>
        <CoverContainer>
          <EntityLink onClick={() => onClick?.(content.entityId)} reference={content}>
            {providers.coverImage({
              responsiveVariants: {
                desktopUp: "size640x360",
                mobileUp: "size640x360",
                tabletUp: "size640x360",
              },
            })}
          </EntityLink>
        </CoverContainer>
      </Wrapper>
    </Container>
  );
};

Component.displayName = "FeedViewArticle";

export const FeedViewArticle = withHighlightedHeadline(Component);
